import { type FormEvent, useState } from 'react';
import { useAdminLocale } from '../lib/admin-locale.js';
import { readJson, useRemote } from '../lib/remote.js';
import { Panel } from './Panel.js';
import { useConfirm } from './ui/ConfirmDialog.js';
import {
  Badge,
  Button,
  Card,
  Field,
  Icon,
  PagedTableShell,
  Select,
  Table,
  Td,
  TextInput,
  useClientPaging,
} from './ui/index.js';

interface MemberRow {
  userId: string;
  email: string;
  role: 'TENANT_ADMIN' | 'TENANT_VIEWER';
  status: 'INVITED' | 'ACTIVE' | 'DISABLED';
}

/**
 * Member list with invitation, role change and removal. `tenantId` lets a platform admin manage a
 * client's members from the tenant page; otherwise the selected tenant applies.
 */
export function MembersPanel(props: {
  canWrite: boolean;
  tenantId?: string | undefined;
  currentUserId?: string | undefined;
}) {
  const { text } = useAdminLocale();
  const confirmDialog = useConfirm();
  const remote = useRemote<MemberRow[]>('/v1/members', props.tenantId);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRow['role']>('TENANT_VIEWER');
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [pending, setPending] = useState(false);
  const allRows = remote.data ?? [];
  const paging = useClientPaging(allRows);
  const rows = paging.rows;

  async function invite(event: FormEvent): Promise<void> {
    event.preventDefault();
    setPending(true);
    setNotice(null);
    try {
      await readJson(
        '/v1/members/invitations',
        { method: 'POST', body: JSON.stringify({ email, role }) },
        props.tenantId,
      );
      setEmail('');
      setNotice({
        tone: 'ok',
        text: text(
          'Η πρόσκληση καταχωρήθηκε. Ο σύνδεσμος αποδοχής στέλνεται με email και ισχύει μία φορά.',
          'The invitation was created. A single-use acceptance link is sent by email.',
        ),
      });
      await remote.mutate();
    } catch (error) {
      setNotice({
        tone: 'error',
        text:
          error instanceof Error ? error.message : text('Η πρόσκληση απέτυχε', 'Invitation failed'),
      });
    } finally {
      setPending(false);
    }
  }

  function changeRole(userId: string, next: MemberRow['role']): void {
    setNotice(null);
    void readJson(
      `/v1/members/${userId}`,
      { method: 'PATCH', body: JSON.stringify({ role: next }) },
      props.tenantId,
    )
      .then(() => remote.mutate())
      .catch((error: unknown) => {
        setNotice({
          tone: 'error',
          text:
            error instanceof Error
              ? error.message
              : text('Η αλλαγή ρόλου απέτυχε', 'Role change failed'),
        });
      });
  }

  function remove(member: MemberRow): void {
    void (async () => {
      const ok = await confirmDialog({
        title: text('Αφαίρεση μέλους', 'Remove member'),
        message: text(
          `Αφαίρεση του ${member.email} από τον οργανισμό;`,
          `Remove ${member.email} from the organization?`,
        ),
        confirmLabel: text('Αφαίρεση', 'Remove'),
        danger: true,
      });
      if (!ok) return;
      setNotice(null);
      try {
        await readJson(`/v1/members/${member.userId}`, { method: 'DELETE' }, props.tenantId);
        await remote.mutate();
      } catch (error: unknown) {
        setNotice({
          tone: 'error',
          text:
            error instanceof Error ? error.message : text('Η αφαίρεση απέτυχε', 'Removal failed'),
        });
      }
    })();
  }

  return (
    <div className="space-y-4">
      {props.canWrite ? (
        <Card
          title={text('Πρόσκληση μέλους', 'Invite member')}
          description={text(
            'Ο προσκεκλημένος ορίζει δικό του κωδικό.',
            'The invitee sets their own password.',
          )}
        >
          <form onSubmit={(event) => void invite(event)} className="flex flex-wrap items-end gap-4">
            <div className="min-w-0 flex-1 basis-64">
              <Field label="Email">
                <TextInput
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}

                  placeholder="name@example.com"
                />
              </Field>
            </div>
            <div className="w-full sm:w-48">
              <Field label={text('Ρόλος', 'Role')}>
                <Select
                  className="mt-1.5"
                  value={role}
                  onChange={(next) => setRole(next as MemberRow['role'])}
                  options={[
                    { value: 'TENANT_VIEWER', label: text('Προβολή', 'Viewer') },
                    { value: 'TENANT_ADMIN', label: text('Διαχειριστής', 'Administrator') },
                  ]}
                />
              </Field>
            </div>
            <Button type="submit" disabled={pending} className="w-full sm:w-auto">
              <Icon name="mail" className="size-4" />
              {pending ? text('Αποστολή…', 'Sending…') : text('Πρόσκληση', 'Invite')}
            </Button>
          </form>
          {notice ? (
            <p
              className={`mt-3 text-sm ${notice.tone === 'ok' ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}
            >
              {notice.text}
            </p>
          ) : null}
        </Card>
      ) : notice ? (
        <p className="text-sm text-rose-700 dark:text-rose-300">{notice.text}</p>
      ) : null}
      <Panel
        loading={!remote.data && !remote.error}
        error={remote.error?.message ?? null}
        empty={allRows.length === 0}
        emptyLabel={text('Δεν υπάρχουν μέλη.', 'There are no members.')}
      >
        <Card flush>
          <PagedTableShell pagination={paging.total > 0 ? paging.pager : null}>
            <Table
              head={
                props.canWrite
                  ? [
                      text('Μέλος', 'Member'),
                      text('Κατάσταση', 'Status'),
                      text('Ρόλος', 'Role'),
                      '',
                    ]
                  : [text('Μέλος', 'Member'), text('Κατάσταση', 'Status'), text('Ρόλος', 'Role')]
              }
            >
              {rows.map((member) => {
                const self = member.userId === props.currentUserId;
                // Open invitations are not memberships yet, so role/removal do not apply.
                const editable = props.canWrite && !self && member.status !== 'INVITED';
                return (
                  <tr key={member.userId}>
                    <Td>
                      <span className="flex items-center gap-3">
                        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-600">
                          <Icon name="users" className="size-4" />
                        </span>
                        <span className="font-medium text-slate-900 dark:text-slate-100">
                          {member.email}
                          {self ? (
                            <span className="ml-2 text-xs text-slate-400 dark:text-slate-500">
                              {text('εσείς', 'you')}
                            </span>
                          ) : null}
                        </span>
                      </span>
                    </Td>
                    <Td>
                      <Badge
                        value={member.status}
                        label={
                          member.status === 'ACTIVE'
                            ? text('Ενεργό', 'Active')
                            : member.status === 'INVITED'
                              ? text('Εκκρεμεί πρόσκληση', 'Invitation pending')
                              : text('Απενεργό', 'Disabled')
                        }
                      />
                    </Td>
                    <Td>
                      {editable ? (
                        <Select
                          aria-label={text(`Ρόλος για ${member.email}`, `Role for ${member.email}`)}
                          value={member.role}
                          onChange={(next) => changeRole(member.userId, next as MemberRow['role'])}
                          options={[
                            { value: 'TENANT_VIEWER', label: text('Προβολή', 'Viewer') },
                            { value: 'TENANT_ADMIN', label: text('Διαχειριστής', 'Administrator') },
                          ]}
                        />
                      ) : (
                        <Badge
                          value={member.role}
                          label={
                            member.role === 'TENANT_ADMIN'
                              ? text('Διαχειριστής', 'Administrator')
                              : text('Προβολή', 'Viewer')
                          }
                        />
                      )}
                    </Td>
                    {props.canWrite ? (
                      <Td className="text-right">
                        {editable ? (
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => remove(member)}
                            aria-label={text(`Αφαίρεση ${member.email}`, `Remove ${member.email}`)}
                            title={text('Αφαίρεση', 'Remove')}
                          >
                            <Icon name="trash" className="size-4 text-rose-600" />
                          </Button>
                        ) : null}
                      </Td>
                    ) : null}
                  </tr>
                );
              })}
            </Table>
          </PagedTableShell>
        </Card>
      </Panel>
    </div>
  );
}
